import React, { useEffect, useState } from 'react';
import MainView from '../views/MainView';
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import { Box, Grid } from '@mui/material';
import Event from './Event';
import { Calendar as PrimeCalendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { TabView, TabPanel } from 'primereact/tabview';
import { Button } from 'primereact/button';
import { useUser } from '../../context/UserContext';
import CustomSnackbar from '../../utils/customsnackbar';

const Calendar = ({openDialogComponent = () => {}, setIsBlur = () => {}, backgroundColor = '#5D3B9E', userSettings = {}, update = () => {}, refresh = () => {}}) => {
    const authCtx = useUser();
    const apiUrl = process.env.REACT_APP_API_URL;
    const [tzOffset, setTzOffset] = useState(null);
    const [events, setEvents] = useState([]);
    const [calendarSelection, setCalendarSelection] = useState(null);
    const [eventSelection, setEventSelection] = useState(null);
    const [newEvent, setNewEvent] = useState({title: '', contextText: '', description: '', type: 'context'});
    const [dateRange, setDateRange] = useState(null);
    const [snackAlert, setSnackAlert] = useState({ open: false, severity: 'success', snackText: '', autoHideDuration: 3000 });
    const eventColors = {
        context: {
            backgroundColor: '#42A5F5', // Blue
            borderColor: '#1E88E5',
            textColor: '#fff'
        },
        reminder: {
            backgroundColor: '#66BB6A', // Green
            borderColor: '#388E3C',
            textColor: '#fff'
        },
        private: {
            backgroundColor: '#FFCA28', // Yellow
            borderColor: '#F57F17',
            textColor: '#000'
        }
    };
    const frequencyOptions = [
        { label: 'None', value: '' },
        { label: 'Every X Days', value: 'day' },
        { label: 'Every X Weeks', value: 'week' },
        { label: 'Every X Months', value: 'month' }
    ];

    const convertUTCToLocal = (dateString) => {
        const date = new Date(dateString);
        return new Date(date.getTime() + tzOffset * 60000);
    };
    const convertLocalToUTCISOString = (localDate) => {
        return new Date(localDate.getTime() - tzOffset * 60000).toISOString();
    };

    const handleDateClick = (selected) => {
        setEventSelection(null);
        setCalendarSelection(selected);
        setDateRange([selected.start, selected.end]);
        setNewEvent({title: '', contextText: '', description: '', type: 'context', repeat: null});
    };
    const handleEventClick = (selected) => {
        setCalendarSelection(null);
        setEventSelection(selected);
        const event = selected.event;
        const start = (event.start);
        const end = (event.end);
        const {originalId = event.id, type = 'private', description = '', contextText = '', repeat = null} = event.extendedProps || {};
        setNewEvent({title: event.title, description, contextText, type, repeat});
        setDateRange([start, end]);
        event.id = originalId;
    };
    const handleCreateEvent = async () => {
        const [start, end] = dateRange || [];
        const id = `event-${Date.now()}`;
        const event = new Event(id, newEvent.title.slice(0, 50), (start), (end || start), calendarSelection?.allDay, 
            newEvent.type, false, newEvent.contextText.slice(0, 500), newEvent.description.slice(0, 500), newEvent.repeat || null);
        try {
            const url = `${apiUrl}/user/events?token=${authCtx.token}`;
            const options = {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(event)
            };
            const res = await fetch(url, options);
            if (res.ok) {
                setEvents(prev => [...prev, event]);
                setSnackAlert({open: true, severity: 'success', snackText: 'Event created!', autoHideDuration: 3000});
                resetForm();
            } else {
                throw new Error('Failed to create event.');
            }
        } catch (err) {
            setSnackAlert({open: true, severity: 'error', snackText: err.message, autoHideDuration: 5000});
        }
    };
    const handleUpdateEvent = async () => {
        const [start, end] = dateRange || [];
        const id = eventSelection.event.id.split('_')[0];
        const updatedEvent = new Event(id, newEvent.title.slice(0, 50), (start), (end || start),
            true, newEvent.type, false, newEvent.contextText.slice(0, 500), newEvent.description.slice(0, 500), newEvent.repeat || null);
        try {
            const url = `${apiUrl}/user/events/${id}?token=${authCtx.token}`;
            const options = {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updatedEvent)
            };
            const res = await fetch(url, options);
            if (res.ok) {
                setEvents(prev => prev.map(evt => evt.id === id ? updatedEvent : evt));
                setSnackAlert({open: true, severity: 'success', snackText: 'Event updated!', autoHideDuration: 3000});
                resetForm();
            } else {
                throw new Error('Failed to update event.');
            }
        } catch (err) {
            setSnackAlert({open: true, severity: 'error', snackText: err.message, autoHideDuration: 5000});
        }
    };
    const handleDeleteEvent = async () => {
        const id = eventSelection.event.id.split('_')[0];
        try {
            const url = `${apiUrl}/user/events/${id}?token=${authCtx.token}`;
            const options = {
                method: 'DELETE'
            };
            const res = await fetch(url, options);
            if (res.ok) {
                setEvents(prev => prev.filter(evt => evt.id !== id));
                setSnackAlert({open: true, severity: 'success', snackText: 'Event deleted!', autoHideDuration: 3000});
                resetForm();
            } else {
                throw new Error('Failed to delete event.');
            }
        } catch (err) {
            setSnackAlert({open: true, severity: 'error', snackText: err.message, autoHideDuration: 5000});
        }
    };
    const handleDragEventUpdate = async (changeInfo) => {
        const updated = changeInfo.event;
        const updatedEvent = new Event(updated.id.split('_')[0], updated.title.slice(0, 50), (updated.start),
            (updated.end || updated.start), updated.allDay, updated.extendedProps.type, false,
            updated.extendedProps.contextText.slice(0, 500), updated.extendedProps.description.slice(0, 500), updated.extendedProps.repeat || null);
        try {
            const url = `${apiUrl}/user/events/${updated.id.split('_')[0]}?token=${authCtx.token}`;
            const options = {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updatedEvent)
            };
            const res = await fetch(url, options);
            if (res.ok) {
                setEvents(prev => prev.map(e => (e.id === updated.id.split('_')[0] ? updatedEvent : e)));
                setSnackAlert({open: true, severity: 'success', snackText: 'Event moved successfully.', autoHideDuration: 3000});
            } else {
                throw new Error('Failed to update event after drag.');
            }
        } catch (err) {
            setSnackAlert({open: true, severity: 'error', snackText: err.message, autoHideDuration: 5000});
        }
    };
    const handleResizeEventUpdate = async (resizeInfo) => {
        const updated = resizeInfo.event;
        const updatedEvent = new Event(updated.id.split('_')[0], updated.title.slice(0, 50), (updated.start),
            (updated.end || updated.start), updated.allDay, updated.extendedProps.type, false,
            updated.extendedProps.contextText.slice(0, 500), updated.extendedProps.description.slice(0, 500), updated.extendedProps.repeat || null);
        try {
            const url = `${apiUrl}/user/events/${updated.id.split('_')[0]}?token=${authCtx.token}`;
            const options = {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updatedEvent)
            };
            const res = await fetch(url, options);
            if (res.ok) {
                setEvents(prev => prev.map(e => (e.id === updated.id.split('_')[0] ? updatedEvent : e)));
                setSnackAlert({open: true, severity: 'success', snackText: 'Event duration updated.', autoHideDuration: 3000});
            } else {
                throw new Error('Failed to update event duration.');
            }
        } catch (err) {
            setSnackAlert({open: true, severity: 'error', snackText: err.message, autoHideDuration: 5000});
        }
    };
    const handleFrequencyDropdown = (e) => {
        const unit = e.value;
        setNewEvent((prev) => ({...prev, repeat: unit ? { interval: 1, unit, until: null } : null}));
    };
    const handleIntervalInputChange = (e) => {
        setNewEvent((prev) => ({...prev, repeat: {...prev.repeat, interval: e.value || 1}}));
    };
    const handleFrequencyUntilChange = (e) => {
        setNewEvent((prev) => ({...prev, repeat: {...prev.repeat, until: e.value ? e.value.toISOString() : null}}));
    };

    const resetForm = () => {
        setEventSelection(null);
        setCalendarSelection(null);
        setDateRange(null);
        setNewEvent({title: '', contextText: '', description: '', type: 'context'});
    };
    const expandRecurringEvents = (events) => {
        const expanded = [];
        const now = new Date();
        const safeLimit = new Date();
        safeLimit.setMonth(now.getMonth() + 12);
        for (const evt of events) {
            if (!evt.repeat) { 
                expanded.push(evt); 
                continue; 
            }
            const {interval, unit, until} = evt.repeat;
            const start = new Date(evt.start);
            const end = new Date(evt.end);
            const untilDate = until ? new Date(until) : safeLimit;
            let currentStart = new Date(start);
            let currentEnd = new Date(end);
            while (currentStart <= untilDate) {
                expanded.push({
                    ...evt,
                    id: `${evt.id}_${currentStart.toISOString()}`,
                    start: new Date(currentStart),
                    end: new Date(currentEnd),
                    extendedProps: {...evt, originalId: evt.id}
                });
                switch (unit) {
                    case 'day':
                        currentStart.setDate(currentStart.getDate() + interval);
                        currentEnd.setDate(currentEnd.getDate() + interval);
                        break;
                    case 'week':
                        currentStart.setDate(currentStart.getDate() + 7 * interval);
                        currentEnd.setDate(currentEnd.getDate() + 7 * interval);
                        break;
                    case 'month':
                        currentStart.setMonth(currentStart.getMonth() + interval);
                        currentEnd.setMonth(currentEnd.getMonth() + interval);
                        break;
                }
            }
        }
        return expanded;
    };

    const loadEvents = async () => {
        try {
            const url = `${apiUrl}/user/events?token=${authCtx.token}`;
            const res = await fetch(url);
            const data = await res.json();
            if (res.ok) {
                setEvents(data.events);
                setSnackAlert({open: true, severity: 'success', snackText: 'Events loaded successfully!', autoHideDuration: 3000});
            } else {
                throw new Error(data.error || 'Failed to load events.');
            }
        } catch (err) {
            setSnackAlert({open: true, severity: 'error', snackText: err.message, autoHideDuration: 5000});
        }
    };

    useEffect(() => {
        const localOffset = new Date().getTimezoneOffset();
        setTzOffset(localOffset);
        loadEvents();
    }, []);

    return (
        <MainView allowScroll={false}>
            <CustomSnackbar {...snackAlert} setSnackAlert={setSnackAlert} />
            <Grid container spacing={4} sx={{height: '650px'}}>
                <Grid item xs={8}>
                    <FullCalendar plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]} initialView='dayGridMonth' height='600px'
                        headerToolbar={{left: 'prev,next today', center: 'title', right: 'dayGridMonth,timeGridWeek,timeGridDay,listMonth'}}
                        editable selectable selectMirror dayMaxEvents select={handleDateClick} eventClick={handleEventClick} eventDrop={handleDragEventUpdate}
                        eventResize={handleResizeEventUpdate}
                        events={expandRecurringEvents(events).map(evt => {
                            const { backgroundColor, borderColor, textColor } = eventColors[evt.type] || {};
                            return {
                                ...evt,
                                start: (evt.start),
                                end: (evt.end),
                                backgroundColor,
                                borderColor,
                                textColor,
                                extendedProps: {
                                    type: evt.type,
                                    description: evt.description,
                                    contextText: evt.contextText
                                }
                            };
                        })}
                    />
                </Grid>
                <Grid item xs={4}>
                    {(calendarSelection || eventSelection) ? (
                        <Box>
                            <h1 style={{margin: '0px', padding: '0px'}}>{eventSelection ? 'Edit Event' : 'Create New Event'}</h1>
                            <Box sx={{width: '100%', maxHeight: '70vh', overflowY: 'auto', paddingRight: '16px'}}>
                                <h2 style={{margin: '0px', padding: '0px'}}>Date Range</h2>
                                <PrimeCalendar value={dateRange} onChange={(e) => setDateRange(e.value)} selectionMode='range' readOnlyInput
                                    hideOnRangeSelection showTime={!calendarSelection?.allDay} showOnFocus={false} style={{ width: '100%' }} />
                                <Box sx={{marginTop: '16px'}}>
                                    <h2 style={{margin: '0px', padding: '0px'}}>Repeat Options</h2>
                                    {/* Repeat frequency dropdown */}
                                    <Dropdown value={newEvent.repeat?.unit || ''} onChange={handleFrequencyDropdown} options={frequencyOptions} 
                                        placeholder="Repeat interval" style={{width: '100%', marginBottom: '1rem', marginTop: '0px'}} />
                                    {/* Interval input */}
                                    {newEvent.repeat?.unit && (
                                        <React.Fragment>
                                            <h2 style={{margin: '0px', padding: '0px'}}>Interval</h2>
                                            <InputNumber value={newEvent.repeat.interval} onValueChange={handleIntervalInputChange} useGrouping={false}
                                                min={1} max={365} placeholder={`Repeat every X ${newEvent.repeat.unit}${newEvent.repeat.interval > 1 ? 's' : ''}`}
                                                style={{ width: '100%', marginBottom: '1rem' }} />
                                        </React.Fragment>
                                    )}
                                    {/* Until date picker */}
                                    {newEvent.repeat?.unit && (
                                        <React.Fragment>
                                            <h2 style={{margin: '0px', padding: '0px'}}>Repeat Until</h2>
                                            <PrimeCalendar value={newEvent.repeat.until ? new Date(newEvent.repeat.until) : null} onChange={handleFrequencyUntilChange}
                                                showIcon placeholder="Repeat until (optional)" style={{ width: '100%' }} showButtonBar />
                                        </React.Fragment>
                                    )}
                                </Box>
                                <h2 style={{margin: '0px', padding: '0px', marginTop: '16px'}}>Title</h2>
                                <InputText placeholder='Title' maxLength={50} style={{ width: '100%', marginTop: '0px' }}
                                    value={newEvent.title} onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })} />
                                <h2 style={{margin: '0px', padding: '0px', marginTop: '16px'}}>Description</h2>
                                <InputTextarea autoResize maxLength={500} placeholder='Description' style={{ width: '100%', marginTop: '0px' }}
                                    value={newEvent.description} onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })} />
                                <h2 style={{margin: '0px', padding: '0px', marginTop: '16px'}}>Event Type</h2>
                                <TabView activeIndex={['context', 'reminder', 'private'].indexOf(newEvent.type)}
                                    onTabChange={(e) => setNewEvent({ ...newEvent, type: ['context', 'reminder', 'private'][e.index] })}>
                                    <TabPanel header="Context">
                                        <p>Context provides the bot with information to use in chat responses.</p>
                                        <InputTextarea autoResize maxLength={500} placeholder='Context' style={{ width: '100%', marginTop: '16px' }}
                                            value={newEvent.contextText} onChange={(e) => setNewEvent({ ...newEvent, contextText: e.target.value })} />
                                    </TabPanel>
                                    <TabPanel header="Reminder">
                                        <p>A reminder will post a message in your chat if you are live during the duration.</p>
                                        <InputTextarea autoResize maxLength={500} placeholder='Chat message' style={{ width: '100%', marginTop: '16px' }}
                                            value={newEvent.contextText} onChange={(e) => setNewEvent({ ...newEvent, contextText: e.target.value })} />
                                    </TabPanel>
                                    <TabPanel header="Private">
                                        <p>This event does not affect the bot and is only for your logging.</p>
                                        <InputTextarea autoResize maxLength={500} placeholder='Notes' style={{ width: '100%', marginTop: '16px' }}
                                            value={newEvent.contextText} onChange={(e) => setNewEvent({ ...newEvent, contextText: e.target.value })} />
                                    </TabPanel>
                                </TabView>
                                <Button label={eventSelection ? 'Save Changes' : 'Create Event'} severity={eventSelection ? 'info' : 'success'}
                                    style={{ width: '50%', marginTop: '16px' }} onClick={eventSelection ? handleUpdateEvent : handleCreateEvent}
                                    disabled={!newEvent.title || !dateRange} />
                                {eventSelection && (
                                    <Button label='Delete Event' severity='danger' style={{width: '50%', marginTop: '16px'}} onClick={handleDeleteEvent} />
                                )}
                            </Box>
                        </Box>
                    ) : (
                        <p>Nothing Selected</p>
                    )}
                </Grid>
            </Grid>
            <Grid container spacing={0} sx={{margin: '0px', padding: '0px'}}>
                <Grid item xs={2}>
                    <Button label='Calendar Settings' onClick={() => openDialogComponent('calendarSettings')} 
                        icon={!userSettings.calendarEvents.timeZone && 'pi pi-exclamation-circle'}
                        tooltip={!userSettings.calendarEvents.timeZone && 'You have not selected your timezone yet'} tooltipOptions={{position: 'top'}} />
                </Grid>
                <Grid item xs={2}>
                    <Button label='Refresh Calendar' onClick={() => loadEvents()} />
                </Grid>
            </Grid>
        </MainView>
    );
};

export default Calendar;